import React, { useState, useEffect, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { Button } from 'react-bootstrap';
import { PedidoModification } from '../../../../Types';
import { localDB } from '../../../../../../../../crud/localDB/localDB';
import { formatCurrency } from '../../../../../../../../utils/functions';
import './Graphs.scss';

interface ModificationsLineTrendsProps {
  data: PedidoModification[];
}

interface AxisData {
  date: Date;
  label: string;
  startDate?: string;
  endDate?: string; 
}

interface UserModificationData {
  usuario: string;
  negativeCount: number;
  totalAmount: number;
}


const GraficosHistModific: React.FC<ModificationsLineTrendsProps> = ({ data }) => {
  const [timeRange, setTimeRange] = useState<'1w' | '1m' | '6m' | '1y'>('6m');
  const [xAxisData, setXAxisData] = useState<AxisData[]>([]);
  const [colorMode, setColorMode] = useState<string>('dark');
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Load initial color mode from LocalDB
    const loadColorMode = async () => {
      try {
        const mode = await localDB.get<string>('ui', 'colorMode');
        console.log('Loaded colorMode from LocalDB:', mode);
        setColorMode(mode || 'dark');
      } catch (error) {
        console.warn('Failed to load colorMode from LocalDB:', error);
        setColorMode('dark');
      }
    };
    
    loadColorMode();
  }, []);

  const textColor = colorMode === 'light' ? '#6b6b6b' : '#FFFFFF';
  const backgroundColor = 'transparent';

  useEffect(() => {
    const now = new Date();
    let arr: AxisData[] = [];
    switch (timeRange) {
      case '1w':
        for (let i = 6; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(now.getDate() - i);
          arr.push({
            date: d,
            label: d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }),
          });
        }
        break;
      case '1m':
        for (let i = 29; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(now.getDate() - i);
          arr.push({
            date: d,
            label: d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }),
          });
        }
        break;
      case '6m':
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          arr.push({
            date: d,
            label: d.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' }),
          });
        }
        break;
      case '1y':
        for (let i = 11; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          arr.push({
            date: d,
            label: d.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' }),
          });
        }
        break;
      default:
        arr = [];
    }
    setXAxisData(arr);
  }, [timeRange]);

  // Filtra los datos según el rango de tiempo
  const filteredData = data.filter((mod) => {
    const modDate = new Date(mod.timestamp);
    const now = new Date();
    switch (timeRange) {
      case '1w':
        return modDate >= new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
      case '1m':
        return modDate >= new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      case '6m':
        return modDate >= new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      case '1y':
        return modDate >= new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      default:
        return true;
    }
  });



  const processedData = useMemo(() => {
    return xAxisData.map(({ date }) => {
      const relevant = filteredData.filter((mod) => {
        const d = new Date(mod.timestamp);
        if (timeRange === '1w' || timeRange === '1m') {
          return (
            d.getDate() === date.getDate() &&
            d.getMonth() === date.getMonth() &&
            d.getFullYear() === date.getFullYear()
          );
        }
        if (timeRange === '6m' || timeRange === '1y') {
          return (
            d.getMonth() === date.getMonth() &&
            d.getFullYear() === date.getFullYear()
          );
        }
        return false;
      });

      const negative = relevant.filter(mod => mod.isnegative);
      const positive = relevant.filter(mod => !mod.isnegative);
      
      // Calculate negative amounts
      const negativeAmount = negative.reduce((sum, mod) => {
        const amount = mod.modification_amount || 0;
        return sum + Math.abs(amount);
      }, 0);

      const positiveAmount = positive.reduce((sum, mod) => {
        const amount = mod.modification_amount || 0;
        return sum + Math.abs(amount);
      }, 0);

      return {
        totalCount: relevant.length,
        negativeCount: negative.length,
        positiveCount: positive.length,
        negativeAmount,
        positiveAmount,
        totalAmount: negativeAmount + positiveAmount
      };
    });
  }, [xAxisData, filteredData, timeRange]);
  
  const totalMods = processedData.map(d => d.totalCount);
  const negativeMods = processedData.map(d => d.negativeCount);
  const negativeAmounts = processedData.map(d => d.negativeAmount);
  const totalAmounts = processedData.map(d => d.totalAmount);

  const seriesNames = ['Total Cantidad', 'Negativas Cantidad', 'Negativo Monto ($)', 'Total Monto ($)'];
  const seriesData = [totalMods, negativeMods, negativeAmounts, totalAmounts];
  
  // Modern color palette inspired by GraficoVentas
  const CHART_COLORS = [
    '#FC7556', // MedioPunto Orange - Total
    '#ef4444', // Modern Red - Negatives
    '#f59e0b', // Amber - Negative Amounts  
    '#22c55e', // Modern Green - Total Amounts
  ];

  const lineOption = {
    color: CHART_COLORS,
    backgroundColor: 'transparent',
    animation: true,
    animationDuration: 1200,
    animationEasing: 'cubicInOut' as const,
    animationDelayUpdate: 300,
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: {
        type: 'cross' as const,
        lineStyle: {
          color: '#FC7556',
          width: 2,
          type: 'dashed' as const
        },
        crossStyle: {
          color: '#FC7556'
        },
        label: {
          backgroundColor: '#FC7556',
          color: '#ffffff',
          fontWeight: 600,
          borderRadius: 6,
          padding: [8, 12]
        }
      },
      backgroundColor: colorMode === 'dark' ? 'rgba(17, 24, 39, 0.95)' : 'rgba(249, 250, 251, 0.95)',
      borderColor: colorMode === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(209, 213, 219, 0.3)',
      borderWidth: 1,
      borderRadius: 12,
      textStyle: {
        color: textColor,
        fontWeight: 500,
      },
      padding: [12, 16],
      extraCssText: `box-shadow: ${colorMode === 'dark' 
        ? '0 10px 25px rgba(0, 0, 0, 0.3)'
        : '0 10px 25px rgba(0, 0, 0, 0.1)'};`,
      formatter: function(params: any) {
        if (!Array.isArray(params)) return '';
        
        let html = `<div style="font-weight: 600; margin-bottom: 8px; color: ${textColor};">${params[0].axisValue}</div>`;
        params.forEach((param: any) => {
          const isAmount = param.seriesName.includes('Monto');
          const value = isAmount ? formatCurrency(param.value) : param.value;
          html += `
            <div style="display: flex; align-items: center; margin-bottom: 4px;">
              <span style="
                display: inline-block; 
                width: 12px; 
                height: 12px; 
                border-radius: 50%; 
                background: ${param.color}; 
                margin-right: 8px;
                box-shadow: 0 0 0 2px ${backgroundColor};
              "></span>
              <span style="font-weight: 600; color: ${textColor};">
                ${param.seriesName}: ${value}
              </span>
            </div>
          `;
        });
        return html;
      }
    },
    legend: {
      data: seriesNames,
      top: isMobile ? 'auto' : 5,
      bottom: isMobile ? 5 : 'auto',
      left: 'center',
      textStyle: { 
        color: colorMode === 'light' ? '#374151' : '#F3F4F6',
        fontWeight: 500,
        fontSize: isMobile ? 9 : 11,
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      },
      orient: 'horizontal' as const,
      itemWidth: 10,
      itemHeight: 10,
      itemGap: isMobile ? 8 : 15,
      icon: 'circle',
      formatter: function(name: string) {
        // Shorten legend names for mobile
        if (isMobile) {
          return name.replace('Total Cantidad', 'Total').replace('Negativas Cantidad', 'Negativas').replace('Negativo Monto ($)', 'Neg.$').replace('Total Monto ($)', 'Tot.$');
        }
        return name;
      }
    },
    grid: {
      left: '4%',
      right: '4%',
      bottom: isMobile ? '25%' : '8%',
      top: isMobile ? '8%' : '18%',
      containLabel: true
    },
    xAxis: {
      type: 'category' as const,
      data: xAxisData.map((item) => item.label),
      boundaryGap: false,
      axisLine: {
        lineStyle: {
          color: colorMode === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.6)',
          width: 1
        }
      },
      axisTick: { show: false },
      axisLabel: {
        color: colorMode === 'light' ? '#374151' : '#F3F4F6',
        rotate: isMobile ? 30 : 0,
        fontSize: isMobile ? 10 : 11,
        fontWeight: 500,
        margin: 12,
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      }
    },
    yAxis: [
      {
        type: 'value' as const,
        name: 'Cantidad',
        position: 'left' as const,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: colorMode === 'light' ? '#374151' : '#F3F4F6',
          fontSize: 11,
          fontWeight: 500,
          margin: 12,
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        },
        splitLine: {
          lineStyle: {
            color: colorMode === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.6)',
            type: 'dashed' as const,
            width: 1
          }
        }
      },
      {
        type: 'value' as const,
        name: 'Monto ($)',
        position: 'right' as const,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: colorMode === 'light' ? '#374151' : '#F3F4F6',
          fontSize: 11,
          fontWeight: 500,
          margin: 12,
          formatter: (val: number) => formatCurrency(val),
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        },
        splitLine: { show: false }
      }
    ],
    series: seriesData.map((data, idx) => ({
      name: seriesNames[idx],
      type: 'line' as const,
      yAxisIndex: idx >= 2 ? 1 : 0, // Use right axis for amounts
      data,
      smooth: 0.3,
      connectNulls: true,
      lineStyle: {
        width: 3.5,
        color: CHART_COLORS[idx],
        shadowColor: `${CHART_COLORS[idx]}40`,
        shadowBlur: 8,
        shadowOffsetY: 2
      },
      areaStyle: idx < 2 ? {
        color: new (echarts as any).graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: `${CHART_COLORS[idx]}25` },
          { offset: 0.7, color: `${CHART_COLORS[idx]}10` },
          { offset: 1, color: `${CHART_COLORS[idx]}05` }
        ])
      } : undefined,
      itemStyle: {
        color: CHART_COLORS[idx],
        borderWidth: 3,
        borderColor: backgroundColor,
        shadowColor: `${CHART_COLORS[idx]}60`,
        shadowBlur: 6
      },
      emphasis: {
        scale: true,
        scaleSize: 12,
        itemStyle: { shadowBlur: 10, shadowColor: `${CHART_COLORS[idx]}80` }
      },
      symbolSize: 8,
      symbol: 'circle'
    }))
  };

  // Enhanced pie chart: negative modifications aggregated by user
  const userModifications = useMemo(() => {
    const userMap = new Map<string, UserModificationData>();
    
    filteredData.forEach((mod) => {
      if (mod.isnegative && mod.user_name) {
        const existing = userMap.get(mod.user_name);
        const amount = Math.abs(mod.modification_amount || 0);
        
        if (existing) {
          existing.negativeCount += 1;
          existing.totalAmount += amount;
        } else {
          userMap.set(mod.user_name, {
            usuario: mod.user_name,
            negativeCount: 1,
            totalAmount: amount
          });
        }
      }
    });
    
    return Array.from(userMap.values())
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 8); // Top 8 users
  }, [filteredData]);

  const pieData = userModifications.map((user, index) => ({
    name: user.usuario,
    value: user.totalAmount,
    count: user.negativeCount,
    itemStyle: {
      color: new (echarts as any).graphic.LinearGradient(0, 0, 1, 1, [
        { offset: 0, color: CHART_COLORS[index % CHART_COLORS.length] },
        { offset: 1, color: `${CHART_COLORS[index % CHART_COLORS.length]}CC` }
      ])
    }
  }));

  // Typed legend props to satisfy ECharts types
  const legendType: 'plain' | 'scroll' = isMobile ? 'scroll' : 'plain';
  const legendOrient: 'horizontal' | 'vertical' = isMobile ? 'horizontal' : 'vertical';

  const pieOption: echarts.EChartsOption = {
    color: CHART_COLORS,
    backgroundColor: 'transparent',
    animation: true,
    animationDuration: 1000,
    animationEasing: 'cubicOut' as const,
    tooltip: {
      trigger: 'item' as const,
      backgroundColor: colorMode === 'dark' ? 'rgba(17, 24, 39, 0.95)' : 'rgba(249, 250, 251, 0.95)',
      borderColor: colorMode === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(209, 213, 219, 0.3)',
      borderWidth: 1,
      borderRadius: 12,
      textStyle: {
        color: textColor,
        fontWeight: 500,
      },
      padding: [12, 16],
      extraCssText: `box-shadow: ${colorMode === 'dark' 
        ? '0 10px 25px rgba(0, 0, 0, 0.3)'
        : '0 10px 25px rgba(0, 0, 0, 0.1)'};`,
      formatter: function(params: any) {
        return `
          <div style="font-weight: 600; margin-bottom: 6px; color: ${textColor};">
            ${params.name}
          </div>
          <div style="display: flex; align-items: center; margin-bottom: 4px;">
            <span style="
              display: inline-block; 
              width: 12px; 
              height: 12px; 
              border-radius: 50%; 
              background: ${params.color}; 
              margin-right: 8px;
              box-shadow: 0 0 0 2px ${backgroundColor};
            "></span>
            <span style="font-weight: 700;">
              ${formatCurrency(params.value)} (${params.percent}%)
            </span>
          </div>
          <div style="font-size: 0.9em; color: ${textColor}; opacity: 0.8;">
            ${params.data.count} modificaciones negativas
          </div>
        `;
      }
    },
    legend: {
      // Keep legend clearly below the pie on mobile and prevent wrapping overlap
      type: legendType,
      orient: legendOrient,
      left: isMobile ? 'center' : 8,
      top: isMobile ? 'auto' : 'middle',
      bottom: isMobile ? 8 : 'auto',
      // Small padding so there is visible separation from the chart
      padding: isMobile ? [8, 0, 0, 0] : 0,
      textStyle: { 
        color: colorMode === 'light' ? '#374151' : '#F3F4F6',
        fontWeight: 500,
        fontSize: isMobile ? 9 : 11,
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      },
      itemWidth: 10,
      itemHeight: 10,
      itemGap: isMobile ? 8 : 10,
      icon: 'circle' as const,
      formatter: function(name: string) {
        // Truncate long names for better display
        if (name.length > 12) {
          return name.substring(0, 10) + '...';
        }
        return name;
      }
    },
    series: [
      {
        name: 'Modificaciones negativas por usuario',
        type: 'pie' as const,
  // Make the pie larger on mobile but keep it high enough to avoid overlapping the legend
  radius: isMobile ? ['42%', '74%'] : ['40%', '70%'],
  center: isMobile ? ['50%', '34%'] : ['60%', '50%'],
        avoidLabelOverlap: false,
        padAngle: 2,
        itemStyle: {
          borderRadius: 6,
          borderColor: backgroundColor,
          borderWidth: 3,
          shadowBlur: 8,
          shadowColor: 'rgba(0, 0, 0, 0.2)'
        },
        label: {
          show: false
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 16,
            fontWeight: 700,
            color: colorMode === 'light' ? '#374151' : '#F3F4F6',
            formatter: '{b}\n{d}%',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segue UI", sans-serif'
          },
          scale: true,
          scaleSize: 8,
          itemStyle: {
            shadowBlur: 15,
            shadowColor: 'rgba(0, 0, 0, 0.3)'
          }
        },
        labelLine: {
          show: false
        },
        data: pieData
      }
    ]
  };

  return (
    <div className="modifications-graphs-root">
      {/* Modern Time Range Selector */}
      <div className="time-range-selector-container">
        <div className="time-range-group">
          <Button
            variant="none"
            className={`time-range-button ${timeRange === '1w' ? 'active' : ''}`}
            onClick={() => setTimeRange('1w')}
          >
            1S
          </Button>
          <Button
            variant="none"
            className={`time-range-button ${timeRange === '1m' ? 'active' : ''}`}
            onClick={() => setTimeRange('1m')}
          >
            1M
          </Button>
          <Button
            variant="none"
            className={`time-range-button ${timeRange === '6m' ? 'active' : ''}`}
            onClick={() => setTimeRange('6m')}
          >
            6M
          </Button>
          <Button
            variant="none"
            className={`time-range-button ${timeRange === '1y' ? 'active' : ''}`}
            onClick={() => setTimeRange('1y')}
          >
            1A
          </Button>
        </div>
      </div>

      {/* Enhanced Charts Layout */}
      <div className="charts-row">
        <div className="chart-wrapper">
          <div className="chart-header">
            Tendencia de Modificaciones - Cantidad y Montos
          </div>
          <div className="chart-container">
            <ReactECharts 
              option={lineOption} 
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
            />
          </div>
        </div>
        
        {!isMobile && (
          <div className="chart-wrapper">
            <div className="chart-header">
              Modificaciones Negativas por Usuario
            </div>
            <div className="chart-container">
              <ReactECharts 
                option={pieOption} 
                style={{ height: '100%', width: '100%' }}
                opts={{ renderer: 'canvas' }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Mobile Pie Chart */}
      {isMobile && (
        <div className="chart-wrapper mobile-chart">
          <div className="chart-header">
            Modificaciones Negativas por Usuario
          </div>
          <div className="chart-container">
            <ReactECharts 
              option={pieOption} 
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GraficosHistModific;
